import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import MonthlyStepperTopHeader from './MonthlyStepperTopHeader';
import MonthlyStepperFooter from './MonthlyStepperFooter';
import styles from './MonthlyStepper.module.scss';

import { getMonthNavigation, getSumByKey, getYearlyMonthListByCount, numberFormat, parseYearRange } from './utils';
import { useCalendarSlideTransition } from './useCalendarSlideTransition';

import {
  IDatasource,
  MonthNavigation,
  YearMonthEntryValue,
  StepperAction,
  YearMonthEntryList,
  IYearlySource,
  IHeaderRow,
} from './types';
import { STEPPER_CONFIG } from './config';
import useLocalizationResolver from '../../hooks/useLocalizationResolver';
import { Spinner } from 'react-bootstrap';

import moment from 'moment';

type Props = {
  yearRange?: any[];
  datasource: IDatasource;
  headerTitle: string;
  enableAnimation?: boolean;
  loading?: boolean;
  onNextMonth?: (range: [string, string]) => void;
  onPreviousMonth?: (range: [string, string]) => void;
};
const YEAR_BOUNDARY = ['2022-01-01', '2028-12-31'];

export const MonthlyStepper = ({
  yearRange,
  datasource,
  headerTitle,
  enableAnimation = true,
  loading = false,
  onNextMonth,
  onPreviousMonth,
}: Props) => {
  const computedYearRange = React.useMemo(() => {
    if (!yearRange || yearRange.length === 0) {
      const end = moment().subtract(1, 'months').endOf('month');
      const start = moment().subtract(6, 'months').startOf('month');
      return [start.format('YYYY-MM'), end.format('YYYY-MM')];
    }
    return yearRange.map((d) => moment(d, ['YYYY-MM-DD', 'YYYY-MM']).format('YYYY-MM'));
  }, [yearRange && yearRange.join(',')]);

  const { slideDirection, triggerSlide, isAnimating } = useCalendarSlideTransition(enableAnimation);
  const { getLang } = useLocalizationResolver();
  const [selectedHeaderMonths, setSelectedHeaderMonths] = useState<Array<any>>([]);
  const [selectedMonths, setSelectedMonths] = useState<YearMonthEntryList[]>([]);
  const [selectedSummaryTotals, setSelectedSummaryTotals] = useState<YearMonthEntryValue[]>([]);
  const [actionType, setActionType] = useState<StepperAction | null>(null);

  const headerRows: IHeaderRow[] = datasource.headers || [];
  const datasourceCollection: IYearlySource[] = datasource.reports || [];

  const yearBoundary = useMemo(() => parseYearRange(computedYearRange), [computedYearRange.join(',')]);
  const [yearOffset, yearLimit] = yearBoundary;

  const initializeSelectedPeriodData = () => {
    const initalMonths = getYearlyMonthListByCount(STEPPER_CONFIG.displayColumns, yearOffset, yearLimit);
    setSelectedHeaderMonths(initalMonths);
  };

  const updateSelectedYearlyMonthDetails = () => {
    const selectedCalendarRecords = selectedHeaderMonths
      .map((value) => {
        const foundYearCollection = datasourceCollection.find((item) => {
          return item.year === value.year;
        });

        return {
          year: value.year,
          monthIndex: value.monthIndex,
          monthName: value.monthName,
          inputs: headerRows.map((headerItem) => {
            if (!foundYearCollection) {
              return {
                monthIndex: value.monthIndex,
                name: headerItem.name,
                value: 0,
              };
            }
            const dataset = foundYearCollection.dataset.find((ds) => ds.name === headerItem.name);
            if (!dataset) {
              return {
                monthIndex: value.monthIndex,
                name: headerItem.name,
                value: 0,
              };
            }
            const periodDataset = dataset.months.find((recordItem) => recordItem.monthIndex === value.monthIndex);
            return {
              monthIndex: value.monthIndex,
              name: headerItem.name,
              value: periodDataset && typeof periodDataset.value === 'number' ? periodDataset.value : 0,
            };
          }),
        };
      })
      .filter((value) => value);

    const selectedCalendarSummaryRecords = selectedCalendarRecords.map((item) => {
      return {
        year: item!.year,
        monthIndex: item!.monthIndex,
        monthName: item!.monthName,
        value: getSumByKey(item!.inputs, 'value'),
      };
    });

    setSelectedMonths(selectedCalendarRecords);
    setSelectedSummaryTotals(selectedCalendarSummaryRecords);
  };

  const handleNext = () => {
    if (!selectedHeaderMonths.length) return;
    const callCallbackWithNewRange = (months: MonthNavigation[]) => {
      if (typeof onNextMonth === 'function') {
        const first = months[0];
        const last = months[months.length - 1];
        const start = moment({ year: first.year, month: first.monthIndex - 1, day: 1 }).format('YYYY-MM-01');
        const end = moment({ year: last.year, month: last.monthIndex - 1 })
          .endOf('month')
          .format('YYYY-MM-DD');
        onNextMonth([start, end]);
      }
    };
    if (enableAnimation) {
      triggerSlide('left');
      setActionType(StepperAction.FORWARD);
      setTimeout(() => {
        const lastMonth = selectedHeaderMonths[selectedHeaderMonths.length - 1];
        let nextMonths: MonthNavigation[] = [];
        let tempMonth = { ...lastMonth };
        for (let i = 0; i < 3; i++) {
          tempMonth = getMonthNavigation({
            year: tempMonth.year,
            monthNumber: tempMonth.monthIndex,
            type: 'next',
          });
          nextMonths.push({
            year: tempMonth.year,
            monthIndex: tempMonth.monthIndex,
            monthName: tempMonth.monthName,
          });
        }
        setSelectedHeaderMonths((prev) => {
          let newDataList = [...prev];
          newDataList = newDataList.slice(3); // Remove first 3
          newDataList = [...newDataList, ...nextMonths];
          callCallbackWithNewRange(newDataList);
          return newDataList;
        });
      }, 400); // match CSS duration
    } else {
      const lastMonth = selectedHeaderMonths[selectedHeaderMonths.length - 1];
      let nextMonths: MonthNavigation[] = [];
      let tempMonth = { ...lastMonth };
      for (let i = 0; i < 3; i++) {
        tempMonth = getMonthNavigation({
          year: tempMonth.year,
          monthNumber: tempMonth.monthIndex,
          type: 'next',
        });
        nextMonths.push({
          year: tempMonth.year,
          monthIndex: tempMonth.monthIndex,
          monthName: tempMonth.monthName,
        });
      }
      setSelectedHeaderMonths((prev) => {
        let newDataList = [...prev];
        newDataList = newDataList.slice(3); // Remove first 3
        newDataList = [...newDataList, ...nextMonths];
        callCallbackWithNewRange(newDataList);
        return newDataList;
      });
      setActionType(StepperAction.FORWARD);
    }
  };

  const handlePrevious = () => {
    if (!selectedHeaderMonths.length) return;
    const callCallbackWithNewRange = (months: MonthNavigation[]) => {
      if (typeof onPreviousMonth === 'function') {
        const first = months[0];
        const last = months[months.length - 1];
        const start = moment({ year: first.year, month: first.monthIndex - 1, day: 1 }).format('YYYY-MM-01');
        const end = moment({ year: last.year, month: last.monthIndex - 1 })
          .endOf('month')
          .format('YYYY-MM-DD');
        onPreviousMonth([start, end]);
      }
    };
    if (enableAnimation) {
      triggerSlide('right');
      setActionType(StepperAction.BACKWARD);
      setTimeout(() => {
        const firstMonth = selectedHeaderMonths[0];
        let prevMonths: MonthNavigation[] = [];
        let tempMonth = { ...firstMonth };
        for (let i = 0; i < 3; i++) {
          tempMonth = getMonthNavigation({
            year: tempMonth.year,
            monthNumber: tempMonth.monthIndex,
            type: 'prev',
          });
          // unshift to reverse order for correct display
          prevMonths.unshift({
            year: tempMonth.year,
            monthIndex: tempMonth.monthIndex,
            monthName: tempMonth.monthName,
          });
        }
        setSelectedHeaderMonths((prev) => {
          let newDataList = [...prev];
          newDataList = newDataList.slice(0, newDataList.length - 3); // Remove last 3
          newDataList = [...prevMonths, ...newDataList];
          callCallbackWithNewRange(newDataList);
          return newDataList;
        });
      }, 400);
    } else {
      const firstMonth = selectedHeaderMonths[0];
      let prevMonths: MonthNavigation[] = [];
      let tempMonth = { ...firstMonth };
      for (let i = 0; i < 3; i++) {
        tempMonth = getMonthNavigation({
          year: tempMonth.year,
          monthNumber: tempMonth.monthIndex,
          type: 'prev',
        });
        // unshift to reverse order for correct display
        prevMonths.unshift({
          year: tempMonth.year,
          monthIndex: tempMonth.monthIndex,
          monthName: tempMonth.monthName,
        });
      }
      setSelectedHeaderMonths((prev) => {
        let newDataList = [...prev];
        newDataList = newDataList.slice(0, newDataList.length - 3); // Remove last 3
        newDataList = [...prevMonths, ...newDataList];
        callCallbackWithNewRange(newDataList);
        return newDataList;
      });
      setActionType(StepperAction.BACKWARD);
    }
  };

  useEffect(() => {
    initializeSelectedPeriodData();
  }, []);

  useEffect(() => {
    if (!selectedHeaderMonths || selectedHeaderMonths.length === 0) {
      const initalMonths = getYearlyMonthListByCount(STEPPER_CONFIG.displayColumns, yearOffset, yearLimit);
      setSelectedHeaderMonths(initalMonths);
    }
  }, [yearOffset, yearLimit]);

  useEffect(() => {
    const initalMonths = getYearlyMonthListByCount(STEPPER_CONFIG.displayColumns, yearOffset, yearLimit);
    setSelectedHeaderMonths(initalMonths);
  }, [computedYearRange.join(',')]);

  useEffect(() => {
    updateSelectedYearlyMonthDetails();
  }, [selectedHeaderMonths, datasourceCollection]);

  return (
    <div className="total-cost-stepper">
      {loading && (
        <div className="stepper-loading-overlay">
          <Spinner animation="border" role="status" className="spinner-indicator-overlay-color" />
        </div>
      )}
      <table>
        <tr>
          <td className="table-left fs-name theme-text table-first-col" valign="bottom">
            {headerTitle}
          </td>
          <td className="table-right">
            <MonthlyStepperTopHeader
              selectedHeaderMonths={selectedHeaderMonths}
              yearRange={YEAR_BOUNDARY}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              isAnimating={isAnimating}
              enableAnimation={enableAnimation}
            ></MonthlyStepperTopHeader>
          </td>
        </tr>

        {headerRows.map((header, headerIdx) => (
          <tr key={header.name}>
            <td className="table-left">{getLang(header.title)}</td>
            <td className="table-right table-body">
              <table className="border-spacing-0">
                <tr
                  className={classNames({
                    [styles['calendar-slide']]: enableAnimation,
                    [styles['slide-left']]: enableAnimation && slideDirection === 'left',
                    [styles['slide-right']]: enableAnimation && slideDirection === 'right',
                  })}
                >
                  {selectedMonths.map((month, index) => (
                    <td key={index} className={classNames('text-right')}>
                      {numberFormat(month.inputs[headerIdx]?.value || 0)}
                    </td>
                  ))}
                </tr>
              </table>
            </td>
          </tr>
        ))}

        <tr className="last-table-row">
          <td className="table-left">{STEPPER_CONFIG.footerTitle}</td>
          <td className="table-right table-body">
            <MonthlyStepperFooter
              selectedHeaderMonths={selectedHeaderMonths}
              selectedSummaryTotals={selectedSummaryTotals}
              actionType={actionType}
              slideDirection={enableAnimation ? slideDirection : ''}
              enableAnimation={enableAnimation}
            ></MonthlyStepperFooter>
          </td>
        </tr>
      </table>
    </div>
  );
};

export default React.memo(MonthlyStepper);
